import React, { Suspense, useEffect } from 'react';
import { lazyWithRetry as lazy } from '@/lib/lazy-with-retry';
import { useRoute, useLocation } from 'wouter';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import V2Routes from './app/v2-routes';
import SupportWidgetMount from './components/v2/Support/SupportWidgetMount';
import { usePageViewTracking } from './lib/v2/usePageViewTracking';

/**
 * Replace the current URL with `to` and render nothing. Used to retire
 * legacy public pages that have v2 equivalents while preserving inbound
 * links and bookmarks.
 */
function Redirect({ to }: { to: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to, navigate]);
  return null;
}

// Loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-[var(--bg-surface)]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-[var(--accent-secondary)] border-t-[var(--accent-primary)] rounded-full animate-spin" />
    </div>
  </div>
);

// Eagerly loaded pages (needed immediately).
// Note: LandingPage, Login, Signup were retired in favor of the v2 surface,
// which now serves the clean top-level paths (/, /login, /signup, ...).
import ResetPassword from './pages/ResetPassword';

// Lazy loaded pages (loaded on demand). CVTailor / ProfileAnalysis / Admin*
// are imported by their V2 wrappers in client/src/pages/v2/, not here, so
// the legacy /app/cv, /app/profile-analysis and /admin* paths only redirect
// rather than render the V1 chrome.
const DashboardHome = lazy(() => import('./pages/DashboardHome'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Profile = lazy(() => import('./pages/Profile'));
const Tokens = lazy(() => import('./pages/Tokens'));
const Payment = lazy(() => import('./pages/Payment'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));

// New Pages
// Pricing / Privacy / Terms / About / Refund / Contact are served directly
// by the v2 surface at their clean paths (see V2Routes).
const Analytics = lazy(() => import('./pages/Analytics'));
const Blog = lazy(() => import('./pages/Blog'));
const Posts = lazy(() => import('./pages/Posts'));
const MyTickets = lazy(() => import('./pages/MyTickets'));

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  // Defer navigate() to an effect so we don't update one router during the
  // render of another. Unauthenticated users land on /login.
  useEffect(() => {
    if (!loading && !user) {
      // Use the browser to avoid coupling to wouter's setLocation during render.
      window.history.replaceState({}, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg-surface)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[var(--accent-secondary)] border-t-[var(--accent-primary)] rounded-full animate-spin" />
          <p className="text-[var(--text-secondary)]">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  // The v2 surface now owns the clean top-level paths (/, /login, /home,
  // /pricing, ...) and is the DEFAULT branch below. Legacy /v2/* URLs are
  // stripped client-side here (vercel.json also 308-redirects them at the
  // edge for full page loads — this catches in-app navigations).
  const [location] = useLocation();
  const matchLegacyV2 = location === '/v2' || location.startsWith('/v2/');

  // First-party page-view tracking → page_views table (real Landing/Pricing
  // traffic for the admin dashboard, independent of PostHog).
  usePageViewTracking();

  // Legacy v1 routes — every hook runs on every render (rules of hooks),
  // branching happens after the declarations.
  const [matchAppHome] = useRoute('/app');
  const [matchAppSetup] = useRoute('/app/setup');
  const [matchAppLinkedin] = useRoute('/app/linkedin');  // redirect → /analyze
  const [matchAppCv] = useRoute('/app/cv');
  const [matchAppCampaignsList] = useRoute('/app/campaigns');
  const [matchAppCampaignsNew] = useRoute('/app/campaigns/new');
  const [matchAppCampaignsReport] = useRoute('/app/campaigns/:id');
  const [matchAppTokens] = useRoute('/app/tokens');
  const [matchAppPayment] = useRoute('/app/payment');
  const [matchAppComingSoon] = useRoute('/app/coming-soon');
  const [matchAppProfile] = useRoute('/app/profile');
  const [matchReset] = useRoute('/reset-password');

  // Legacy admin sub-pages (the v2 surface serves /admin itself now).
  const [matchAdminUsers] = useRoute('/admin/users');
  const [matchAdminSettings] = useRoute('/admin/settings');

  const [matchBlog] = useRoute('/blog');
  const [matchAppAnalytics] = useRoute('/app/analytics');
  const [matchAppProfileAnalysis] = useRoute('/app/profile-analysis');
  const [matchAppPosts] = useRoute('/app/posts');
  const [matchAppTickets] = useRoute('/app/tickets');
  const [matchAppAdmin] = useRoute('/app/admin');

  // Safety net: strip the legacy /v2 prefix and replace, preserving the
  // query string and hash (e.g. /v2/checkout/success?id=x → /checkout/success?id=x).
  if (matchLegacyV2) {
    const target =
      (location.slice(3) || '/') + window.location.search + window.location.hash;
    return <Redirect to={target} />;
  }

  // Legacy /app/cv, /app/profile-analysis, /admin sub-pages, /app/admin are
  // V2-only. Anyone landing on a legacy path is bounced to the clean path so
  // they never see the old chrome. Other /app/* pages (DashboardHome,
  // Profile, etc.) keep working until they get full V2 equivalents.
  if (matchAppCv)              return <Redirect to="/cvs" />;
  if (matchAppProfileAnalysis) return <Redirect to="/analyze" />;
  if (matchAdminUsers)         return <Redirect to="/admin" />;
  if (matchAdminSettings)      return <Redirect to="/admin" />;
  if (matchAppAdmin)           return <Redirect to="/admin" />;

  // /reset-password is load-bearing for password recovery (Supabase recovery
  // links + the index.html hash-catcher land here). It stays a v1 route and
  // must match BEFORE the V2Routes default branch.
  if (matchReset) return <ResetPassword />;

  if (matchBlog) return <Blog />;

  // Protected app routes
  if (matchAppHome)
    return (
      <ProtectedRoute>
        <DashboardHome />
      </ProtectedRoute>
    );
  if (matchAppSetup)
    return (
      <ProtectedRoute>
        <Onboarding />
      </ProtectedRoute>
    );
  if (matchAppProfile)
    return (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    );
  if (matchAppTokens)
    return (
      <ProtectedRoute>
        <Tokens />
      </ProtectedRoute>
    );
  if (matchAppPayment)
    return (
      <ProtectedRoute>
        <Payment />
      </ProtectedRoute>
    );
  if (matchAppLinkedin) {
    // Legacy /app/linkedin → analyze (kept as a redirect for any
    // outstanding bookmarks).
    return <Redirect to="/analyze" />;
  }
  if (matchAppComingSoon)
    return (
      <ProtectedRoute>
        <ComingSoon />
      </ProtectedRoute>
    );
  if (matchAppCampaignsNew || matchAppCampaignsReport || matchAppCampaignsList) {
    window.location.replace('/app/coming-soon?feature=campaigns');
    return null;
  }

  if (matchAppPosts)
    return (
      <ProtectedRoute>
        <Posts />
      </ProtectedRoute>
    );
  if (matchAppAnalytics)
    return (
      <ProtectedRoute>
        <Analytics />
      </ProtectedRoute>
    );
  if (matchAppTickets)
    return (
      <ProtectedRoute>
        <MyTickets />
      </ProtectedRoute>
    );

  // DEFAULT: the v2 surface serves every other path (/, /login, /home,
  // /pricing, /cvs, /admin, ...). Unknown paths keep V2Routes' existing
  // behavior (it returns null — same as an unknown /v2/* path before).
  return (<><V2Routes /><SupportWidgetMount /></>);
};

const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-red-500">Something went wrong. Please refresh.</div>}>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <AppRoutes />
        </Suspense>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;